import {
  ErrorHandler,
  InjectionToken,
  Injector,
  ModuleWithProviders,
  NgModule
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { DevelopmentModule } from '@content-platform/web-angular-shared-util-development';
import { flatten } from 'lodash';
import { IpAddressService } from './logger/ip-address.service';
import { NewRelicService } from './logger/new-relic.service';
import { LoggerService } from './logger/logger.service';
import { ErrorHandlerService } from './logger/error-handler.service';
import { LoggingEnvironment, LoggingEnvironmentConfig } from './logging-environment.provider';
import { LoggerParamsTransformer, PARAM_TRANSFORMERS } from './models';
import { MetaReducer, USER_PROVIDED_META_REDUCERS } from '@ngrx/store';
import { LOG_ALL_STORE_ACTIONS_FLAG } from './logger/logger-config';
import { getActionLoggingReducer, StoreLoggerService } from './logger/store-logger.service';
import { MegaBeaconService } from './logger/mega-beacon.service';
import { GeolocationService } from './logger/geolocation.service';
import { Configuration } from '@content-platform/web-angular-shared-util-configuration';

export type LoggerParamsTransformerInput =
  | typeof LoggerParamsTransformer
  | (typeof LoggerParamsTransformer)[];

/**
 * This factory allows me to initiate the transformer classes, and provides around this issue:
 *   Function expressions are not supported in decorators
 * This is why I am passing in the un initiated classes through the Injection token below.
 * The factory is only provided in the forRoot, so that would have be called in the main app.
 */
export const INITIAL_TRANSFORMERS_CLASS = new InjectionToken('initial-transformer-class');

export function classFactory(injector, paramTransformers) {
  return (flatten(paramTransformers) as any)
    .filter(paramTransformer => !!paramTransformer)
    .map(paramTransformerClass => {
      return new paramTransformerClass(injector);
    });
}

/**
 * Gets the meta reducers for the store:
 *  Adds the logging meta reducer
 *
 * @export
 * @param {StoreLoggerService} storeLogger
 * @returns {MetaReducer<{}>[]}
 */
export function getMetaReducers(storeLogger: StoreLoggerService): MetaReducer<{}>[] {
  return [getActionLoggingReducer(storeLogger)];
}

/**
 * Constructs the environment object from Configuration options
 */
export function environmentFactory(): LoggingEnvironmentConfig {
  return {
    production: Configuration.isProductionEquivalentEnvironment(),
    version: Configuration.get('version')
  };
}

/**
 * Logging Module
 *
 * @export
 * @class LoggingModule
 */
@NgModule({
  imports: [
    CommonModule,
    HttpClientModule,
    DevelopmentModule.withFlags([...LOG_ALL_STORE_ACTIONS_FLAG])
  ]
})
export class LoggingModule {
  static forRoot(paramTransformer?: LoggerParamsTransformerInput): ModuleWithProviders<LoggingModule> {
    return {
      ngModule: LoggingModule,
      providers: [
        LoggerService,
        NewRelicService,
        StoreLoggerService,
        IpAddressService,
        MegaBeaconService,
        GeolocationService,
        // https://discuss.newrelic.com/t/how-to-monitor-angular-5-application/53309/2
        { provide: ErrorHandler, useClass: ErrorHandlerService },
        { provide: LoggingEnvironment, useFactory: environmentFactory },
        { provide: INITIAL_TRANSFORMERS_CLASS, multi: true, useValue: paramTransformer },
        {
          provide: PARAM_TRANSFORMERS,
          useFactory: classFactory,
          deps: [Injector, INITIAL_TRANSFORMERS_CLASS]
        },
        {
          provide: USER_PROVIDED_META_REDUCERS,
          deps: [StoreLoggerService],
          useFactory: getMetaReducers
        }
      ]
    };
  }

  static withExtraParams(paramTransformer: LoggerParamsTransformerInput): ModuleWithProviders<LoggingModule> {
    return {
      ngModule: LoggingModule,
      providers: [{ provide: INITIAL_TRANSFORMERS_CLASS, multi: true, useValue: paramTransformer }]
    };
  }
}
